/** @jsxImportSource @emotion/react */
import { css, Theme as EmotionTheme } from '@emotion/react';

import { useState, useEffect, useLayoutEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5themes_Kelly from "@amcharts/amcharts5/themes/Kelly";
import { Box, CircularProgress, useTheme } from "@mui/material/";
import { padLeadingZerosToNum, randGenSeed, randWithSeed } from '../../../common/CustomFunctions';
import { useParams } from 'react-router-dom';
import ReportResultService from '../../../service/ReportResultService';
import ErrorMessageContainer from '../../../common/components/ErrorMessageContainer';
import EmptyGraphMessageContainer from '../../../common/components/EmptyGraphMessageContainer';


//This chart is not like the oter ones. This treat each product or actor as its own graph as a series.
//Example you define toActor or fromActor and the actor prop contains multiple actors. product prop is empty.

const chartFrame = css({
    height: "100%",
    width: "100%"
});

export interface IProps {
    //clientId: string,
    year: Date;
    chartName?: string;
    chartTitle?: string;
    removePadding?: boolean;
    preZoomed?: boolean;

}

const BarChartResultReport = (props: IProps): JSX.Element => {

    const theme = useTheme();

    const root = useRef<am5.Root>();
    //##
    const series = useRef<am5xy.ColumnSeries>();
    const xAxis = useRef<am5xy.CategoryAxis<am5xy.AxisRenderer>>();
    //##
    const { organizationId } = useParams();
    const [list, setList] = useState<{ date: string, value: number }[]>([]);
    const [error, setError] = useState(false);
    const [loader, setLoader] = useState(true);
    //##

    let name = "chartBar";

    if (props.chartName !== undefined && props.chartName !== null) {
        name = props.chartName;
    }

    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        setLoader(true);

        const service = new ReportResultService();

        const getTotals = async () => {
            const result = await service.getReportResults(organizationId, props.year.getFullYear());
            if (result.hasErrors) {
                setError(true);
            } else {
                setError(false);
                if (isMounted) {
                    let tempList: { date: string, value: number }[] = []
                    for (let i = 0; i < 12; i++) {
                        tempList.push({
                            date: props.year.getFullYear() + "-" + padLeadingZerosToNum(i+1, 2),
                            value: result.value.totalsRow.periods[i]
                        })
                    }
                    setList(tempList);
                }
            }
            setLoader(false);
        };

        getTotals();

        return () => { isMounted = false };

    }, [organizationId, props.year]);

    useLayoutEffect(() => { //Gets triggerred before useEffect.

        //#################################################################################
        //#################################Root############################################
        let root_ = am5.Root.new(name);
        root_.setThemes([
            am5themes_Animated.new(root_),
            am5themes_Kelly.new(root_),
        ]);
        //root_.dateFormatter.set("dateFormat", "yyyy-MM-dd");
        root_.dateFormatter.setAll({ dateFormat: "yyyy-MM", dateFields: ["valueX"] });
        root_.numberFormatter.set("numberFormat", "#,###.00");

        root.current = root_;
        //#################################################################################
        //#################################Chart###########################################
        let chart_ = root_.container.children.push(am5xy.XYChart.new(root_, {
            panX: false,
            panY: false,
            wheelX: "panX",
            wheelY: "zoomX",
            layout: root_.verticalLayout,
        }));
        if (props.chartTitle !== null && props.chartTitle !== undefined) {
            chart_.topAxesContainer.children.push(am5.Label.new(root_, {
                text: props.chartTitle,
                fontSize: 15,
                fontWeight: "500",
                textAlign: "center",
                x: am5.percent(50),
                centerX: am5.percent(50),
                paddingTop: 0,
                paddingBottom: 0
            }));
        }
        let cursor = chart_.set("cursor", am5xy.XYCursor.new(root_, {
            behavior: "zoomX"
        }));
        cursor.lineY.set("visible", false); //Removes y cursor line.
        //#################################################################################
        //#################################Axis############################################
        let xAxis_ = chart_.xAxes.push(am5xy.CategoryAxis.new(root_, {
            categoryField: "date",
            renderer: am5xy.AxisRendererX.new(root_, {}),
            //tooltip: am5.Tooltip.new(root_, {})
        }));
        xAxis.current = xAxis_;
        

        let yAxis_ = chart_.yAxes.push(am5xy.ValueAxis.new(root_, {
            //min: 0,
            //max: 100,
            //strictMinMax: true,
            //tooltip: am5.Tooltip.new(root_, {}),
            //numberFormat: "#'%'",
            //calculateTotals : true,
            //tooltipNumberFormat: "#'%'",
            renderer: am5xy.AxisRendererY.new(root_, {
                opposite: false
            })
        }));
        //#################################################################################
        //##################################Series#########################################

        let series_ = chart_.series.push(
            am5xy.ColumnSeries.new(root_, {
                name: "result",
                xAxis: xAxis_,
                yAxis: yAxis_,
                valueYField: "value",
                categoryXField: "date",
                tooltip: am5.Tooltip.new(root_, {
                    labelText: "[bold]{valueY}[/]"
                }),
            })
        );

        series_.columns.template.states.create("riseFromPrevious", {
            fill: am5.color(theme.palette.success.main),
            stroke: am5.color(theme.palette.success.main)
        });

        series_.columns.template.states.create("dropFromPrevious", {
            fill: am5.color(theme.palette.error.main),
            stroke: am5.color(theme.palette.error.main)
        });

        series.current = series_;

        //#################################################################################
        //###########################Disable/Enable Stuff##################################
        xAxis_.get("renderer").grid.template.set("forceHidden", true);
        //xAxis_.get("renderer").labels.template.set("forceHidden", true);
        //######
        //yAxis_.get("renderer").grid.template.set("forceHidden", true);
        //yAxis_.get("renderer").labels.template.set("forceHidden", true);
        //######
        if (props.removePadding === true) {
            chart_.setAll({ paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0 });
        }
        //#################################################################################
        //###########################Further Costumization#################################
        //Pre Zoom
        //if (props.preZoomed && props.fromDate && props.toDate) {
        //    seriesList.current.forEach(e => {
        //        e.events.on("datavalidated", function (ev) {

        //            let newFrom = sub(props.toDate, { "years": 4 });
        //            if (props.fromDate && props.fromDate > newFrom)
        //                newFrom = props.fromDate;

        //            xAxis.zoomToDates(newFrom, props.toDate);
        //        })
        //    })
        //}
        //##########################################################
        //########################Legend############################
        chart_.set("layout", root_.verticalLayout);
        let legend_ = chart_.children.push(am5.Legend.new(root_, {
            x: am5.percent(50),
            centerX: am5.percent(50),
            useDefaultMarker: true,
            maxHeight: 4 * parseFloat(getComputedStyle(document.documentElement).fontSize), //Amchart does not understand rem.
            verticalScrollbar: am5.Scrollbar.new(root_, {
                orientation: "vertical"
            })
        }));
        //##########################################################
        //######################Legend Loop#########################

        //seriesList.current.forEach(e => {
        //    legend_.data.push(e);
        //})
        //legend.current = legend_;

        return () => {
            root_.dispose();
        }

    }, []);

    useLayoutEffect(() => {

        if (list.length > 1) {
            xAxis.current?.data.setAll(list);
            series.current?.data.setAll(list);
        }

    }, [list]);

    const ChartRenderMessage = (): JSX.Element => {
        if (loader) {
            return <CircularProgress color="secondary"/>
        } else if (error) {
            return <ErrorMessageContainer />
        } else if (list.length < 1) {
            return <EmptyGraphMessageContainer />
        } else {
            return <></>
        }
    }

    return <Box display="flex" flexDirection="column" alignItems="center" width="100%">
        <ChartRenderMessage/>
        <div id={name} css={chartFrame}>
        </div>
    </Box>
}
export default BarChartResultReport